import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { useUserInterests } from './useInterests';

export interface TrendingTopic {
  interest_id: string;
  interest_name: string;
  interest_category: string | null;
  arena_count: number;
  total_participants: number;
}

export interface ArenaRecommendation {
  arena_id: string;
  arena_name: string;
  arena_topic: string;
  arena_type: string | null;
  match_score: number;
  matching_interests: string[];
  participant_count: number;
}

export interface ArenaTag {
  id: string;
  arena_id: string;
  interest_id: string;
  interest?: {
    id: string;
    name: string;
    category: string | null;
  };
}

export const useArenaTopics = () => {
  const { user } = useAuthContext();
  const { userInterestIds } = useUserInterests();
  const [trendingTopics, setTrendingTopics] = useState<TrendingTopic[]>([]);
  const [recommendations, setRecommendations] = useState<ArenaRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);

  // Fetch trending topics
  const fetchTrendingTopics = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_trending_arena_topics', {
        limit_count: 15,
      });

      if (error) throw error;
      setTrendingTopics((data as TrendingTopic[]) || []);
    } catch (error) {
      console.error('Error fetching trending topics:', error);
      // Fallback to manual query if function doesn't exist yet
      await fetchTrendingTopicsFallback();
    }
  }, []);

  // Fallback method for trending topics
  const fetchTrendingTopicsFallback = async () => {
    try {
      const { data, error } = await supabase
        .from('arena_tags')
        .select(`
          interest_id,
          interest:interests(id, name, category),
          arena:live_arenas!inner(id, is_active)
        `)
        .eq('arena.is_active', true);

      if (error) throw error;

      // Count occurrences manually
      const topicCounts = new Map<string, { name: string; category: string | null; count: number }>();
      
      data?.forEach((tag: any) => {
        if (tag.interest) {
          const existing = topicCounts.get(tag.interest_id);
          if (existing) {
            existing.count++;
          } else {
            topicCounts.set(tag.interest_id, {
              name: tag.interest.name,
              category: tag.interest.category,
              count: 1,
            });
          }
        }
      });

      const trending = Array.from(topicCounts.entries())
        .map(([id, info]) => ({
          interest_id: id,
          interest_name: info.name,
          interest_category: info.category,
          arena_count: info.count,
          total_participants: 0,
        }))
        .sort((a, b) => b.arena_count - a.arena_count)
        .slice(0, 15);

      setTrendingTopics(trending);
    } catch (error) {
      console.error('Error in fallback trending topics:', error);
    }
  };

  // Fetch personalized recommendations
  const fetchRecommendations = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase.rpc('get_arena_recommendations', {
        user_uuid: user.id,
        limit_count: 20,
      });

      if (error) throw error;
      setRecommendations((data as ArenaRecommendation[]) || []);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      // Fallback
      await fetchRecommendationsFallback();
    }
  }, [user]);

  // Fallback method for recommendations
  const fetchRecommendationsFallback = async () => {
    if (!user || userInterestIds.size === 0) return;

    try {
      // Get arenas with their tags
      const { data: arenas, error } = await supabase
        .from('live_arenas')
        .select(`
          id,
          name,
          topic,
          arena_type,
          arena_tags(interest_id, interest:interests(name))
        `)
        .eq('is_active', true)
        .limit(30);

      if (error) throw error;

      const userInterestArray = Array.from(userInterestIds);
      
      const scored = (arenas || []).map((arena: any) => {
        const arenaTags = arena.arena_tags || [];
        const matchingInterests = arenaTags
          .filter((tag: any) => userInterestArray.includes(tag.interest_id))
          .map((tag: any) => tag.interest?.name || '');

        return {
          arena_id: arena.id,
          arena_name: arena.name,
          arena_topic: arena.topic,
          arena_type: arena.arena_type,
          match_score: userInterestArray.length > 0 
            ? Math.round((matchingInterests.length / userInterestArray.length) * 100)
            : 0,
          matching_interests: matchingInterests,
          participant_count: 0,
        };
      });

      setRecommendations(scored.sort((a, b) => b.match_score - a.match_score));
    } catch (error) {
      console.error('Error in fallback recommendations:', error);
    }
  };

  // Get tags for a specific arena
  const getArenaTags = useCallback(async (arenaId: string): Promise<ArenaTag[]> => {
    try {
      const { data, error } = await supabase
        .from('arena_tags')
        .select(`
          *,
          interest:interests(id, name, category)
        `)
        .eq('arena_id', arenaId);

      if (error) throw error;
      return (data as ArenaTag[]) || [];
    } catch (error) {
      console.error('Error fetching arena tags:', error);
      return [];
    }
  }, []);

  // Add tags to an arena
  const addArenaTags = useCallback(async (arenaId: string, interestIds: string[]): Promise<boolean> => {
    if (!user) return false;

    try {
      const inserts = interestIds.map((interestId) => ({
        arena_id: arenaId,
        interest_id: interestId,
      }));

      const { error } = await supabase
        .from('arena_tags')
        .upsert(inserts, { onConflict: 'arena_id,interest_id' });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error adding arena tags:', error);
      return false;
    }
  }, [user]);

  // Remove tag from arena
  const removeArenaTag = useCallback(async (arenaId: string, interestId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('arena_tags')
        .delete()
        .eq('arena_id', arenaId)
        .eq('interest_id', interestId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing arena tag:', error);
      return false;
    }
  }, []);

  // Filter arenas by selected topics
  const toggleTopicFilter = useCallback((topicId: string) => {
    setSelectedTopics((prev) =>
      prev.includes(topicId)
        ? prev.filter((id) => id !== topicId)
        : [...prev, topicId]
    );
  }, []);

  const clearTopicFilters = useCallback(() => {
    setSelectedTopics([]);
  }, []);

  // Initial fetch
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchTrendingTopics(), fetchRecommendations()]);
      setLoading(false);
    };
    load();
  }, [fetchTrendingTopics, fetchRecommendations]);

  // Refresh when user interests change
  useEffect(() => {
    if (user && userInterestIds.size > 0) {
      fetchRecommendations();
    }
  }, [userInterestIds, user, fetchRecommendations]);

  return {
    trendingTopics,
    recommendations,
    loading,
    selectedTopics,
    toggleTopicFilter,
    clearTopicFilters,
    getArenaTags,
    addArenaTags,
    removeArenaTag,
    refetch: () => Promise.all([fetchTrendingTopics(), fetchRecommendations()]),
  };
};
